import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { ClientJournalTemplate, GlAccount, TemplateLine } from '../types';
import { displayName, findAccount, hiddenFromPicker, pickable, sideMismatch } from '../lib/glChart';
import { PAYROLL_GROUPS, payrollRole, proposeStandardPayroll, removePayrollLine } from '../lib/payrollRoles';

const useStyles = makeStyles({
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  heading: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold },
  muted: { color: tokens.colorNeutralForeground3 },
  small: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  group: { fontWeight: tokens.fontWeightSemibold, backgroundColor: tokens.colorNeutralBackground2 },
  fill: { width: '100%', minWidth: 0 },
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXXS },
  colRole: { width: '26%' },
  colAccount: { width: '30%' },
  colSide: { width: '12%' },
  colNote: { width: '24%' },
  colActions: { width: '8%' },
});

const MAX_NOTE = 300;

export interface PayrollTemplateValue {
  payrollTemplate: ClientJournalTemplate | null;
  accounts: GlAccount[];
  roles: Record<string, string>;
}

/**
 * The client's payroll template on the G/L screen (ptc-docs plan 2026-11-04-conversions-wave-5-payroll §2.1): Payroll
 * Relief's GL Accounts roles, by group. Each line's account IS its mapping (`roles[key]`), picked from the chart. Its side
 * is the role's; where the account's type normally sits on the other side, the line says so. A line with no account posts
 * nothing, and can be removed.
 */
export default function PayrollTemplateSection({ value, onChange }: { value: PayrollTemplateValue; onChange: (next: PayrollTemplateValue) => void }) {
  const styles = useStyles();
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const [matched, setMatched] = useState<number | null>(null);
  const lines = value.payrollTemplate?.lines ?? [];
  const accounts = pickable(value.accounts, showAll);
  const role = (key: string) => t(`glMappingPage.fields.${key}`, { defaultValue: key });
  const labelOf = (line: TemplateLine) => line.label?.trim() || role(line.key);

  function start() {
    const proposed = proposeStandardPayroll(value.roles, value.accounts);
    setMatched(proposed.matched);
    onChange({ ...value, payrollTemplate: proposed.template, roles: proposed.roles });
  }

  function setAccount(key: string, code: string) {
    const { [key]: _was, ...rest } = value.roles;
    onChange({ ...value, roles: code ? { ...rest, [key]: code } : rest });
  }

  function setNote(key: string, note: string) {
    if (!value.payrollTemplate) return;
    const next = value.payrollTemplate.lines.map((l) => (l.key === key ? { ...l, note: note || null } : l));
    onChange({ ...value, payrollTemplate: { lines: next } });
  }

  function remove(key: string) {
    if (!value.payrollTemplate) return;
    const result = removePayrollLine(value.payrollTemplate, value.roles, key);
    onChange({ ...value, payrollTemplate: result.template, roles: result.roles });
  }

  const option = (code: string) => {
    const a = findAccount(value.accounts, code);
    return a && displayName(a) ? `${a.code} · ${displayName(a)}` : code;
  };

  return (
    <section className={styles.section} aria-labelledby="payroll-template-heading">
      <Text as="h2" id="payroll-template-heading" className={styles.heading}>{t('glMappingPage.payroll.heading')}</Text>
      <Text className={styles.muted}>{t(lines.length ? 'glMappingPage.payroll.introOwn' : 'glMappingPage.payroll.introNone')}</Text>

      {lines.length === 0 && (
        <div>
          <Button appearance="primary" onClick={start}>{t('glMappingPage.payroll.start')}</Button>
        </div>
      )}
      {matched !== null && lines.length > 0 && (
        <MessageBar intent="info">
          <MessageBarBody>{t('glMappingPage.payroll.started', { count: matched })}</MessageBarBody>
        </MessageBar>
      )}

      {lines.length > 0 && (
        <>
          {(hiddenFromPicker(value.accounts) > 0 || showAll) && (
            <Switch checked={showAll} onChange={(_e, data) => setShowAll(data.checked)} label={t('glMappingPage.template.showAllAccounts')} />
          )}
          <Table size="small" aria-label={t('glMappingPage.payroll.heading')}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={styles.colRole}>{t('glMappingPage.payroll.columns.role')}</TableHeaderCell>
                <TableHeaderCell className={styles.colAccount}>{t('glMappingPage.template.columns.account')}</TableHeaderCell>
                <TableHeaderCell className={styles.colSide}>{t('glMappingPage.template.columns.side')}</TableHeaderCell>
                <TableHeaderCell className={styles.colNote}>{t('glMappingPage.payroll.columns.note')}</TableHeaderCell>
                <TableHeaderCell className={styles.colActions} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {PAYROLL_GROUPS.flatMap((group) => {
                const inGroup = lines.filter((l) => (payrollRole(l.key)?.group ?? 'expenses') === group);
                if (inGroup.length === 0) return [];
                return [
                  <TableRow key={`group-${group}`}>
                    <TableCell className={styles.group} colSpan={5}>{t(`glMappingPage.payroll.groups.${group}`)}</TableCell>
                  </TableRow>,
                  ...inGroup.map((line) => {
                    const label = labelOf(line);
                    const code = value.roles[line.key] ?? '';
                    const account = code ? findAccount(value.accounts, code) : undefined;
                    const normal = sideMismatch(account, line.side);
                    return (
                      <TableRow key={line.key}>
                        <TableCell>{label}</TableCell>
                        <TableCell>
                          <Select
                            className={styles.fill}
                            size="small"
                            aria-label={t('glMappingPage.template.accountOf', { line: label })}
                            value={code}
                            onChange={(_e, data) => setAccount(line.key, data.value)}
                          >
                            <option value="">{t('glMappingPage.template.notMapped')}</option>
                            {code && !accounts.some((a) => a.code === code) && <option value={code}>{option(code)}</option>}
                            {accounts.map((a) => <option key={a.code} value={a.code}>{option(a.code)}</option>)}
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className={styles.stack}>
                            <span>{t(`glMappingPage.template.sides.${line.side}`)}</span>
                            {normal && account?.type && (
                              <Text className={styles.small}>
                                {t(`glMappingPage.template.normally.${normal}`, {
                                  code: account.code,
                                  type: t(`glMappingPage.chart.types.${account.type}`, { defaultValue: account.type }),
                                })}
                              </Text>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Input
                            className={styles.fill}
                            size="small"
                            maxLength={MAX_NOTE}
                            aria-label={t('glMappingPage.template.noteOf', { line: label })}
                            placeholder={t('glMappingPage.template.notePlaceholder')}
                            value={line.note ?? ''}
                            onChange={(_e, data) => setNote(line.key, data.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            appearance="subtle"
                            aria-label={t('glMappingPage.payroll.removeOf', { line: label })}
                            onClick={() => remove(line.key)}
                          >
                            {t('glMappingPage.payroll.remove')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  }),
                ];
              })}
            </TableBody>
          </Table>
        </>
      )}
    </section>
  );
}
